import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, useToast } from "@cx/ui";
import type { AiAgentSuggestionPage, AiAgentSuggestionView } from "@cx/contracts";
import { AGENT_LIMITS } from "@cx/domain";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import { SuggestionCard } from "./SuggestionCard";

type EnrichResult = { suggestions: AiAgentSuggestionView[] };

/**
 * Cadastros › Cadastro inteligente (T3.7, 06-ia-agentes §3): cole o texto do catálogo do fornecedor ou envie a
 * planilha; cada produto vira uma sugestão (GTIN, NCM e CEST conferidos) e só é cadastrado no aceite.
 */
export function SmartProductPage(): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const queryKey = ["ai-agents", companyId, "enricher"];
  const list = useQuery({
    queryKey,
    queryFn: () => api<AiAgentSuggestionPage>("/ai/agents/suggestions?agent=product_enricher&limit=50"),
  });
  const done = (r: EnrichResult) => {
    toast(
      r.suggestions.length ? "success" : "warning",
      r.suggestions.length
        ? `${r.suggestions.length} produto(s) para revisar.`
        : "Nenhum produto reconhecido. Confira o conteúdo.",
    );
    void qc.invalidateQueries({ queryKey });
  };
  const fromText = useMutation({
    mutationFn: () =>
      api<EnrichResult>("/ai/agents/products/enrich", { method: "POST", body: JSON.stringify({ text }) }),
    onSuccess: (r) => {
      setText("");
      done(r);
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const fromSheet = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return api<EnrichResult>("/ai/agents/products/enrich-sheet", { method: "POST", body: form });
    },
    onSuccess: (r) => {
      if (fileRef.current) fileRef.current.value = "";
      done(r);
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const rows = list.data?.data ?? [];
  return (
    <>
      <ContentHeader title="Cadastro inteligente" breadcrumb={["Cadastros", "Cadastro inteligente"]} />
      <div className="row g-3 mb-3">
        <div className="col-lg-7">
          <div className="box card p-3 h-100">
            <h2 className="h6">Texto do fornecedor</h2>
            <label className="form-label small text-muted" htmlFor="smart-text">
              Cole a lista ou o catálogo (até {AGENT_LIMITS.enricherProducts} produtos). A IA extrai nome,
              GTIN, NCM, marca e atributos; o que estiver em dúvida fica marcado para conferir.
            </label>
            <textarea
              id="smart-text"
              className="form-control mb-2"
              rows={8}
              maxLength={AGENT_LIMITS.enricherChars}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary align-self-start"
              disabled={text.trim().length < 3 || fromText.isPending}
              onClick={() => fromText.mutate()}
            >
              {fromText.isPending ? "Extraindo…" : "Extrair produtos"}
            </button>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="box card p-3 h-100">
            <h2 className="h6">Planilha do fornecedor</h2>
            <p className="small text-muted">
              CSV ou XLSX até 1 MB, com colunas como Nome, SKU, GTIN, NCM, CEST, Unidade, Marca, Preço e
              Custo.
            </p>
            <label className="form-label" htmlFor="smart-file">
              Arquivo
            </label>
            <input
              id="smart-file"
              ref={fileRef}
              type="file"
              className="form-control mb-2"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={fromSheet.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) fromSheet.mutate(f);
              }}
            />
          </div>
        </div>
      </div>
      <h2 className="h6">Produtos para revisar</h2>
      <QueryStatus loading={list.isLoading} error={list.error} />
      {list.isSuccess && rows.length === 0 && (
        <EmptyState
          title="Nada para revisar"
          description="Cole um texto ou envie uma planilha: os produtos aparecem aqui antes de entrar no cadastro."
          actions={[]}
        />
      )}
      {rows.map((s) => (
        <SuggestionCard key={s.id} s={s} queryKey={queryKey} />
      ))}
    </>
  );
}
